// Home block — "Carte du mois" editorial rendezvous. Fetches
// `/api/shop/carte-du-mois` (public, cached by browser); renders nothing
// when no active pick exists so the Home layout stays clean during the
// gap between two picks.
//
// Layout is intentionally asymmetric (image left, copy right) and dark
// so it visually pops after the hero without stealing the CTA. The
// scarcity chip appears only when the admin has set an `end_date` —
// keeping honesty over fake urgency.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock } from "lucide-react";
import { useLang } from "@/lib/i18n";

const COPY = {
  fr: { pill: "La carte du mois", this_month: "Ce mois-ci :", available_until: "Disponible jusqu'au", default_cta: "Découvrir la carte", locale: "fr-FR" },
  en: { pill: "Map of the month", this_month: "This month:", available_until: "Available until", default_cta: "Discover the map", locale: "en-GB" },
  es: { pill: "El mapa del mes", this_month: "Este mes:", available_until: "Disponible hasta", default_cta: "Descubrir el mapa", locale: "es-ES" },
  pt: { pill: "O mapa do mês", this_month: "Este mês:", available_until: "Disponível até", default_cta: "Descobrir o mapa", locale: "pt-PT" },
};

const CarteDuMoisBlock = () => {
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const [carte, setCarte] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/carte-du-mois`, {
          headers: { Accept: "application/json" },
        });
        const data = await r.json();
        if (!cancelled) setCarte(data?.carte || null);
      } catch {
        // Silently skip the block when the endpoint is unreachable.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !carte || !carte.product_id) return null;

  // Scarcity — computed client-side so the label stays live even after
  // the doc is cached. Format follows the user's locale.
  const endLabel = (() => {
    if (!carte.end_date) return null;
    const d = new Date(carte.end_date);
    if (Number.isNaN(d.getTime()) || d < new Date()) return null;
    return d.toLocaleDateString(c.locale, { day: "numeric", month: "long" });
  })();

  return (
    <section
      className="relative bg-[color:var(--gv-ink)] text-white py-16 md:py-24 overflow-hidden"
      data-testid="home-carte-du-mois"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-1.5 bg-[color:var(--gv-pink)]/15 text-[color:var(--gv-pink)] text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full mb-4">
              <Sparkles size={12} /> {c.pill}
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-4" data-testid="cdm-title">
              {c.this_month}<br />
              <span className="text-[color:var(--gv-pink)]">{carte.title}</span>
            </h2>
            {carte.subtitle && (
              <div className="text-[color:rgba(255,255,255,0.65)] text-[13px] uppercase tracking-widest mb-4">
                {carte.subtitle}
              </div>
            )}
            {carte.description && (
              <p className="text-[15px] text-[color:rgba(255,255,255,0.85)] leading-relaxed mb-6">
                {carte.description}
              </p>
            )}
            {endLabel && (
              <div className="inline-flex items-center gap-2 text-[13px] text-[color:rgba(255,255,255,0.7)] mb-6" data-testid="cdm-scarcity">
                <Clock size={13} className="text-[color:var(--gv-pink)]" />
                {c.available_until} <strong className="text-white">{endLabel}</strong>
              </div>
            )}
            <Link
              to={`/shop/${carte.product_id}`}
              className="inline-flex items-center gap-2 bg-white text-[color:var(--gv-ink)] px-5 py-3 rounded-[5px] font-medium hover:bg-[color:var(--gv-pink)] hover:text-white transition-colors"
              data-testid="cdm-cta"
            >
              {carte.cta_label || c.default_cta} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="order-1 md:order-2">
            {carte.cover_url ? (
              <div className="aspect-[4/5] w-full max-w-md mx-auto md:ml-auto rounded-[8px] overflow-hidden shadow-2xl">
                <img src={carte.cover_url} alt={carte.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-[4/5] w-full max-w-md mx-auto bg-white/5 border border-white/10 rounded-[8px]" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CarteDuMoisBlock;
